import React, { useEffect, useRef, useState } from "react"
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  NavItem,
  NavLink,
  TabContent,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  TabPane,
  CardHeader,
  UncontrolledTooltip,
} from "reactstrap"
import PropTypes from "prop-types"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { useLocation } from "react-router-dom"
import { withTranslation } from "react-i18next"
import dayjs from "dayjs"
import { getSupportSms, querySmsLogs } from "helpers/backend_helper"
import CustomRangePicker from "components/DatePickers/RangePicker"
import {
  dateFormatter,
  dateSorter,
  defaultPageSizeOptions,
  downloadExcel,
  showToast,
  stringSorter,
} from "utils/general"
import { Table as AntdTable, InputNumber, Select, Input as AntdInput, } from "antd"
const { Search } = AntdInput
import { tableDefaultHeight } from "constants/layout"

const SmsLogs = props => {
  const [activeTab, setActiveTab] = useState("1")
  const [supportMessages, setSupportMessages] = useState([])
  const [messages, setMessages] = useState([])
  const [ongoingSearch, setOngoingSearch] = useState(false)
  const [message, setMessage] = useState("")
  const [recipient, setRecipient] = useState("")
  const [sender, setSender] = useState("")
  const [source, setSource] = useState("")
  const [statusFilter, setStatusFilter] = useState()
  const [maxRows, setMaxRows] = useState(500)
  const [dateRange, setDateRange] = useState(null)
  const [first, setFirst] = useState(true)
  const [menu, setMenu] = useState(false) //Dropdown button state
  const [searchTriggers, setSearchTriggers] = useState(0) // Delayed search
  const [title] = useState(props.t("SMS logs"))

  const search = useLocation().search
  const abortRef = useRef(null)
  useEffect(() => {
    document.title = title + " | Suvanto Care"
    abortRef.current = new AbortController()
    setDateRange([dayjs().subtract(1, "days"), dayjs()])
    const rec = new URLSearchParams(search).get("recipient")
    if (rec) {
      setRecipient(rec.trim())
    }
    return () => abortRef.current.abort()
  }, [])

  // Get messages on page load
  useEffect(() => {
    if (dateRange && first) {
      setFirst(false)
      getMessages(dateRange[0].valueOf(), dateRange[1].valueOf())
    }
  }, [dateRange])

  useEffect(() => {
    if (dateRange) {
      getMessages(dateRange[0].valueOf(), dateRange[1].valueOf())
    }
  }, [searchTriggers, statusFilter])

  // Get messages
  const getMessages = (startMs, endMs) => {
    if (!ongoingSearch) {
      //console.log("GET SMS: ", startMs, endMs, recipient)
      setOngoingSearch(true)
      querySmsLogs(
        abortRef.current,
        startMs,
        endMs,
        sender,
        recipient,
        message,
        statusFilter?.length > 0 ? Number.parseInt(statusFilter, 10) : undefined,
        source,
        maxRows
      )
        .then(result => {
          //console.log("care: ", result.entries)
          if (result.statusCode === 200 && result.entries != null) {
            setMessages(result.entries)
          } else {
            setMessages([])
          }
          setOngoingSearch(false)
        })
        .catch(err => {
          console.log(err)
          setMessages([])
          setOngoingSearch(false)
        })
    }
  }

  // Get support message
  const getSupportMessages = () => {
    if (!ongoingSearch) {
      setOngoingSearch(true)
      getSupportSms(abortRef.current, false)
        .then(result => {
          //console.log("care: ", result.entries)
          if (result.statusCode === 200 && result.entries != null) {
            setSupportMessages(result.entries)
          } else {
            setSupportMessages([])
          }
          setOngoingSearch(false)
        })
        .catch(err => {
          console.log(err)
          setSupportMessages([])
          setOngoingSearch(false)
        })
    }
  }

  // Table structure
  const columns = [
    {
      dataIndex: "statusTime",
      title: props.t("Updated"),
      sorter: (a, b, sortOrder) => {
        return dateSorter(a["statusTime"], b["statusTime"], sortOrder)
      },
      render: dateFormatter,
      width: "160px",
      ellipsis: true,
    },
    {
      dataIndex: "status",
      title: props.t("Status"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "status", sortOrder)
      },
      render: statusFormatter,
      width: "120px",
      ellipsis: true,
    },
    {
      dataIndex: "message",
      title: props.t("Message"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "message", sortOrder)
      },
      width: "400px",
      ellipsis: true,
    },
    {
      dataIndex: "sender",
      title: props.t("Sender"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "sender", sortOrder)
      },
      width: "180px",
      ellipsis: true,
    },
    {
      dataIndex: "recipient",
      title: props.t("Recipient"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "recipient", sortOrder)
      },
      width: "180px",
      ellipsis: true,
    },
    {
      dataIndex: "source",
      title: props.t("Source"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "source", sortOrder)
      },
      width: "180px",
      ellipsis: true,
    },
  ]

  const columns2 = [
    {
      dataIndex: "statusTime",
      title: props.t("Status updated"),
      sorter: (a, b, sortOrder) => {
        return dateSorter(a["statusTime"], b["statusTime"], sortOrder)
      },
      render: dateFormatter,
      width: "160px",
      ellipsis: true,
    },
    {
      dataIndex: "message",
      title: props.t("Message"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "message", sortOrder)
      },
      width: "400px",
      ellipsis: true,
    },
    {
      dataIndex: "sender",
      title: props.t("Sender"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "sender", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
  ]

  function statusFormatter(cell, row) {
    if (row.status == "RECEIVED" || row.status == "RECVD") {
      return (
        <span className="text-primary">
          <i className="mdi mdi-arrow-left-bold me-1" />
          {cell}
        </span>
      )
    } else if (row.status == "SENT" || row.status == "CREATED") {
      return (
        <span className="text-muted">
          <i className="mdi mdi-arrow-right-bold me-1" />
          {cell}
        </span>
      )
    } else if (row.status == "DELIVERED" || row.status == "DELIVRD") {
      return (
        <span className="text-success">
          <i className="mdi mdi-arrow-right-bold me-1" />
          {cell}
        </span>
      )
    }
    else {
      return (
        <span className="text-danger">
          <i className="mdi mdi-cancel me-1" />
          {cell}
        </span>
      )
    }
  }

  const onDateSelection = value => {
    setDateRange(value)
  }

  // Export values
  const generateExcel = () => {
    const heading = [
      [
        props.t("Updated"),
        props.t("Status"),
        props.t("Message"),
        props.t("Sender"),
        props.t("Recipient"),
        props.t("Source"),
      ],
    ]
    const data = messages.map(elt => [
      dateFormatter(elt.statusTime),
      elt.status,
      elt.message,
      elt.sender,
      elt.recipient,
      elt.source,
    ])

    downloadExcel(title, heading, data)
    showToast(props.t("Excel file exported succesfully!"), "success")
  }

  return (
    <>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title={props.t("SMS logs")}
          />
          <Card>
            <CardHeader className="bg-transparent border-bottom">
              <div className="d-flex flex-wrap">
                <ul className="nav nav-tabs nav-tabs-custom card-header-tabs">
                  <NavItem>
                    <NavLink
                      to="#"
                      active={activeTab === "1"}
                      onClick={() => {
                        setActiveTab("1")
                      }}
                    >
                      {props.t("Search")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      active={activeTab === "2"}
                      onClick={() => {
                        setActiveTab("2")
                      }}
                    >
                      {props.t("Security mail")}
                    </NavLink>
                  </NavItem>
                </ul>
              </div>
            </CardHeader>
            <CardBody>
              <TabContent activeTab={activeTab}>
                <TabPane className="show" tabId="1">
                  <Row>
                    <Col>
                      {messages != null && (
                        <React.Fragment>
                          <Row>
                            <Col className="mb-3">
                              <Select
                                placeholder={props.t("Status")}
                                value={statusFilter}
                                style={{ minWidth: "180px", width: "100%" }}
                                disabled={ongoingSearch}
                                onChange={v => {
                                  setStatusFilter(v)
                                }}
                                allowClear
                                options={[
                                  { value: "-1", label: props.t("Received") },
                                  { value: "0", label: props.t("Sent") },
                                  { value: "1", label: props.t("Delivered") },
                                  { value: "3", label: props.t("Deleted") },
                                  { value: "5", label: props.t("Expired") },
                                  { value: "6", label: props.t("Rejected") },
                                  { value: "7", label: props.t("Failed") },
                                  { value: "11", label: props.t("Unknown") },
                                ]}
                              />
                            </Col>
                            <Col className="mb-3">
                              <Search
                                placeholder={props.t("Message")}
                                disabled={ongoingSearch}
                                allowClear
                                value={message}
                                onChange={v => {
                                  setMessage(v.target.value)
                                }}
                                onSearch={() => {
                                  // onSearch triggers before onChange on clear, delay data fetch a bit
                                  setSearchTriggers(prevValue => prevValue + 1)
                                }}
                                style={{ minWidth: "180px", width: "100%" }}
                              />
                            </Col>
                            <Col className="mb-3">
                              <Search
                                placeholder={props.t("Recipient")}
                                disabled={ongoingSearch}
                                allowClear
                                value={recipient}
                                onChange={v => {
                                  setRecipient(v.target.value)
                                }}
                                onSearch={() => {
                                  // onSearch triggers before onChange on clear, delay data fetch a bit
                                  setSearchTriggers(prevValue => prevValue + 1)
                                }}
                                style={{ minWidth: "180px", width: "100%" }}
                              />
                            </Col>
                            <Col className="mb-3">
                              <Search
                                placeholder={props.t("Sender")}
                                disabled={ongoingSearch}
                                allowClear
                                value={sender}
                                onChange={v => {
                                  setSender(v.target.value)
                                }}
                                onSearch={() => {
                                  // onSearch triggers before onChange on clear, delay data fetch a bit
                                  setSearchTriggers(prevValue => prevValue + 1)
                                }}
                                style={{ minWidth: "180px", width: "100%" }}
                              />
                            </Col>
                            <Col className="mb-3">
                              <Search
                                placeholder={props.t("Source")}
                                disabled={ongoingSearch}
                                allowClear
                                value={source}
                                onChange={v => {
                                  setSource(v.target.value)
                                }}
                                onSearch={() => {
                                  // onSearch triggers before onChange on clear, delay data fetch a bit
                                  setSearchTriggers(prevValue => prevValue + 1)
                                }}
                                style={{ minWidth: "180px", width: "100%" }}
                              />
                            </Col>
                            <Col className="col-auto mb-3">
                              <InputNumber
                                style={{ width: "135px" }}
                                suffix={props.t("max")}
                                disabled={ongoingSearch}
                                value={maxRows}
                                step={100}
                                onChange={newVal => {
                                  setMaxRows(newVal)
                                }}
                                onPressEnter={() => {
                                  setSearchTriggers(prevValue => prevValue + 1)
                                }}
                              />
                            </Col>
                            <Col style={{ maxWidth: "450px" }}>
                              <div className="mb-3" style={{ minWidth: "350px" }}>
                                <CustomRangePicker
                                  value={dateRange}
                                  rangeChanged={onDateSelection}
                                  showTime={true}
                                  allowClear={false}
                                />
                              </div>
                            </Col>
                            <Col className="col-auto">
                              <Button
                                className="compact-button"
                                color="primary"
                                disabled={ongoingSearch}
                                onClick={() => {
                                  getMessages(
                                    dateRange[0].valueOf(),
                                    dateRange[1].valueOf()
                                  )
                                }}
                              >
                                <i className="mdi mdi-database-search me-2" />
                                {props.t("Search")}
                              </Button>
                              <Button
                                className="compact-button ms-3"
                                id="calendar-button"
                                color="primary"
                                disabled={ongoingSearch}
                                onClick={() => {
                                  setDateRange([dateRange[0], dayjs()])
                                  getMessages(
                                    dateRange[0].valueOf(),
                                    dayjs().valueOf()
                                  )
                                }}
                              >
                                <UncontrolledTooltip
                                  placement="bottom"
                                  target="calendar-button"
                                >
                                  {props.t("Refresh time")}
                                </UncontrolledTooltip>
                                <i className="mdi mdi-calendar-refresh" />
                              </Button>
                              <Button
                                className="compact-button ms-3"
                                id="reset-button"
                                color="danger"
                                outline
                                disabled={ongoingSearch}
                                onClick={() => {
                                  setDateRange([dayjs().subtract(1, "days"), dayjs()])
                                  setStatusFilter(null)
                                  setMessage("")
                                  setRecipient("")
                                  setSender("")
                                  setSource("")
                                  setMaxRows(500)
                                }}
                              >
                                <UncontrolledTooltip
                                  placement="bottom"
                                  target="reset-button"
                                >
                                  {props.t("Clear filters")}
                                </UncontrolledTooltip>
                                <i className="mdi mdi-filter-remove" />
                              </Button>
                            </Col>
                            <Col>
                              <span className="float-end ms-2">
                                <UncontrolledTooltip
                                  placement="bottom"
                                  target="downloadstatstable"
                                >
                                  {props.t("Download")}
                                </UncontrolledTooltip>
                                <Dropdown
                                  id="printButtonServicesStats"
                                  isOpen={menu}
                                  toggle={() => setMenu(!menu)}
                                  className="download-btn text-center"
                                  type="button"
                                >
                                  <DropdownToggle
                                    id="downloadstatstable"
                                    className="text-muted"
                                    tag="i"
                                  >
                                    <i className="mdi mdi-download" />
                                  </DropdownToggle>
                                  <DropdownMenu>
                                    <DropdownItem
                                      onClick={() => generateExcel()}
                                    >
                                      {props.t("Download .xlsx")}
                                    </DropdownItem>
                                  </DropdownMenu>
                                </Dropdown>
                              </span>
                              <div className="mt-2 float-end">
                                <p>
                                  {messages.length}{" "}
                                  {props.t("results found")}
                                </p>
                              </div>
                            </Col>
                          </Row>

                          <Row className="mt-1">
                            <Col xl="12">
                              <AntdTable
                                className="colored-header narrow-header"
                                bordered
                                size="small"
                                rowKey={"key"}
                                columns={columns}
                                dataSource={messages}
                                pagination={{
                                  showSizeChanger: true,
                                  defaultPageSize: 50,
                                  pageSizeOptions: defaultPageSizeOptions,
                                }}
                                loading={ongoingSearch}
                                tableLayout="auto"
                                scroll={{
                                  x: "max-content",
                                  y: tableDefaultHeight,
                                }}
                              />
                            </Col>
                          </Row>
                        </React.Fragment>
                      )}
                    </Col>
                  </Row>
                </TabPane>

                <TabPane className="show" tabId="2">
                  <React.Fragment>
                    <Row className="mb-1">
                      <Col className="col-auto mb-3">
                        <Button
                          className="compact-button"
                          color="primary"
                          disabled={ongoingSearch}
                          onClick={() => {
                            getSupportMessages()
                          }}
                        >
                          <i className="mdi mdi-database-search me-2" />
                          {props.t("Search")}
                        </Button>
                      </Col>
                      <Col>
                        <div className="mt-2 float-end">
                          <p>
                            {supportMessages.length} {props.t("results found")}
                          </p>
                        </div>
                      </Col>
                    </Row>

                    <Row className="mt-1">
                      <Col xl="12">
                        <AntdTable
                          className="striped colored-header"
                          bordered
                          size="small"
                          rowKey={"key"}
                          columns={columns2}
                          dataSource={supportMessages}
                          pagination={{
                            showSizeChanger: true,
                            defaultPageSize: 50,
                            pageSizeOptions: defaultPageSizeOptions,
                          }}
                          loading={ongoingSearch}
                          tableLayout="auto"
                          scroll={{
                            x: "max-content",
                            y: tableDefaultHeight,
                          }}
                        />
                      </Col>
                    </Row>
                  </React.Fragment>
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

SmsLogs.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(SmsLogs)
